import useAppStore from "@/context/store";
import { getExpsValue } from "@/tracker/abTest";
import { useCallback, useEffect, useState } from "react";

/**
 * 用于ABtest的hoook
 * @param variableName 平台Key
 * @param defaultValue 默认值
 * @param map 映射表，格式为["映射值"]: "平台值"
 * @example useABTest("test", "default", {planA:"default", planB: "B"})
 * // 返回 {planA: true, planB:false, loading: true}
 */
export default function useABTest<T extends Record<string, string>>(
  variableName: string,
  defaultValue: T[keyof T],
  map?: T
) {
  const user = useAppStore((s) => s.user);
  const [value, setValue] = useState<T[keyof T]>(defaultValue);
  const [loading, setLoading] = useState(true);
  const queryAction = useCallback(async () => {
    setLoading(true);
    const result = await getExpsValue(variableName, defaultValue);
    setLoading(false);
    setValue(result as T[keyof T]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, variableName, user?.user_id]); // 登录用户变化了，可能会导致ab策略变化，所以需要重新获取ab实验结果

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  return Object.entries(map || {}).reduce(
    (result, item) => {
      // @ts-ignore
      result[item[0]] = value === item[1];
      return result;
    },
    {
      loading,
      value,
    }
  ) as Record<keyof T, boolean> & { loading: boolean; value: string };
}

/**
 * 用于获取多个ABtest实验结果的hook
 * @param experiments AB实验配置数组
 * @example
 * useMultiABTest([
 *   { variableName: "test1", defaultValue: "default", map: {planA: "default", planB: "B"} },
 *   { variableName: "test2", defaultValue: "default", map: {planC: "default", planD: "D"} }
 * ])
 * // 返回 { test1: {planA: true, planB: false}, test2: {planC: true, planD: false}, loading: true }
 */
export function useMultiABTest<
  T extends Record<string, Record<string, string>>
>(
  experiments: Array<{
    variableName: string;
    defaultValue: string;
    map?: T[keyof T];
  }>
) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const queryAction = useCallback(async () => {
    setLoading(true);
    const results = await Promise.all(
      experiments.map(({ variableName, defaultValue }) =>
        getExpsValue(variableName, defaultValue).then((result) => ({
          variableName,
          result,
        }))
      )
    );

    setValues(
      results.reduce(
        (acc, { variableName, result }) => ({
          ...acc,
          [variableName]: result,
        }),
        {}
      )
    );
    setLoading(false);
  }, [experiments]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  return {
    ...experiments.reduce((acc, { variableName, map }) => {
      if (!map) return acc;

      acc[variableName] = Object.entries(map).reduce(
        (result, [key, value]) => ({
          ...result,
          [key]: values[variableName] === value,
        }),
        {}
      );
      return acc;
    }, {} as Record<string, Record<string, boolean>>),
    loading,
    values,
  };
}
